import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { State } from '../../types/state'
import { useDispatch, useSelector } from 'react-redux'
import { UserActions } from '../../state/user'
import { isMinimumPackage } from '../../utils/package-validator'
import { Package } from '../../enums/package.enum'

const ChevronDownIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={`tw-w-4 tw-h-4 tw-ml-1.5 tw-transition-transform tw-duration-200 ${className || ''}`} xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor'>
    <path fillRule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.25 4.25a.75.75 0 01-1.06 0L5.23 8.29a.75.75 0 01.02-1.06z' clipRule='evenodd' />
  </svg>
)

const UserIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={`tw-w-5 tw-h-5 ${className || ''}`} xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor'>
    <path fillRule='evenodd' d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-5.5-2.5a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0zM10 12a5.99 5.99 0 00-4.793 2.39A6.483 6.483 0 0010 16.5a6.483 6.483 0 004.793-2.11A5.99 5.99 0 0010 12z' clipRule='evenodd' />
  </svg>
)

const MenuIcon = () => (
  <svg className='tw-w-6 tw-h-6' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={1.5} stroke='currentColor'>
    <path strokeLinecap='round' strokeLinejoin='round' d='M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5' />
  </svg>
)

const CloseIcon = () => (
  <svg className='tw-w-6 tw-h-6' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={1.5} stroke='currentColor'>
    <path strokeLinecap='round' strokeLinejoin='round' d='M6 18L18 6M6 6l12 12' />
  </svg>
)

interface NavItemStructure {
  to?: string
  label: string
  name?: string
  children?: Array<{ to?: string, label: string, action?: () => void }>
  highlight?: boolean
  action?: () => void
}

const Header = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { username, isSuperadmin, package: userPackage, packageExpiredAt } = useSelector((state: State) => state.user)

  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [openDesktopDropdown, setOpenDesktopDropdown] = useState<string | null>(null)
  const [openMobileDropdown, setOpenMobileDropdown] = useState<string | null>(null)
  const [isMobileUserMenuOpen, setIsMobileUserMenuOpen] = useState(false)
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 1024)

  const headerRef = useRef<HTMLElement>(null)
  const mobileMenuRef = useRef<HTMLDivElement>(null)
  const togglerButtonRef = useRef<HTMLButtonElement>(null)

  const dropdownTimerRef = useRef<number | null>(null)
  const DROPDOWN_CLOSE_DELAY = 200

  const isTransparentPage = location.pathname === '/' || location.pathname === '/cooperation' || location.pathname.includes('/designers/')
  const showSolidBgForDesktopOnlyBehavior = !isTransparentPage || (isTransparentPage && isScrolled)
  const actualShowSolidBg = isMobileView || showSolidBgForDesktopOnlyBehavior

  const headerBaseClasses = 'tw-top-0 tw-left-0 tw-right-0 tw-z-50 tw-transition-all tw-duration-300'
  const headerPositioningClasses = isMobileView ? 'tw-sticky' : 'tw-fixed'
  const headerBackgroundClasses = actualShowSolidBg
    ? 'tw-bg-white tw-shadow-md tw-border-b tw-border-slate-200'
    : 'tw-bg-transparent tw-border-b tw-border-white/20'

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50)
    const handleResize = () => {
      const mobileCheck = window.innerWidth < 1024
      if (mobileCheck !== isMobileView) {
        setIsMobileView(mobileCheck)
        if (openDesktopDropdown) setOpenDesktopDropdown(null)
        if (isMobileMenuOpen) setIsMobileMenuOpen(false)
      }
    }

    handleResize()
    window.addEventListener('resize', handleResize)

    if (isTransparentPage && !isMobileView) {
      window.addEventListener('scroll', handleScroll, { passive: true })
      handleScroll()
    } else if (!isMobileView) {
      setIsScrolled(true)
    }

    return () => {
      if (isTransparentPage && !isMobileView) window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
    }
  }, [isTransparentPage, location.pathname, isMobileView, openDesktopDropdown, isMobileMenuOpen])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (isMobileMenuOpen && (mobileMenuRef.current != null) && !mobileMenuRef.current.contains(target) && (togglerButtonRef.current != null) && !togglerButtonRef.current.contains(target)) {
        setIsMobileMenuOpen(false)
      }
      if (openDesktopDropdown && (target.closest(`.desktop-dropdown-wrapper[data-dropdown-name="${openDesktopDropdown}"]`) == null)) {
        if (dropdownTimerRef.current) {
          clearTimeout(dropdownTimerRef.current)
          dropdownTimerRef.current = null
        }
        setOpenDesktopDropdown(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isMobileMenuOpen, openDesktopDropdown])

  useEffect(() => {
    if (dropdownTimerRef.current) {
      clearTimeout(dropdownTimerRef.current)
      dropdownTimerRef.current = null
    }
    setIsMobileMenuOpen(false)
    setOpenDesktopDropdown(null)
    setOpenMobileDropdown(null)
    setIsMobileUserMenuOpen(false)
  }, [location.pathname])

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
    setOpenDesktopDropdown(null)
    setOpenMobileDropdown(null)
    setIsMobileUserMenuOpen(false)
  }

  const handleDesktopDropdownMouseEnter = (dropdownName: string) => {
    if (dropdownTimerRef.current) {
      clearTimeout(dropdownTimerRef.current)
      dropdownTimerRef.current = null
    }
    setOpenDesktopDropdown(dropdownName)
  }

  const handleDesktopDropdownMouseLeave = () => {
    dropdownTimerRef.current = window.setTimeout(() => {
      setOpenDesktopDropdown(null)
    }, DROPDOWN_CLOSE_DELAY)
  }

  const toggleMobileDropdown = (dropdownName: string) => setOpenMobileDropdown(openMobileDropdown === dropdownName ? null : dropdownName)

  const handleMenuItemClick = (path?: string) => {
    if (path) navigate(path)
    if (dropdownTimerRef.current) {
      clearTimeout(dropdownTimerRef.current)
      dropdownTimerRef.current = null
    }
    setIsMobileMenuOpen(false)
    setOpenDesktopDropdown(null)
    setOpenMobileDropdown(null)
    setIsMobileUserMenuOpen(false)
  }

  const handleLogout = (): void => {
    localStorage.removeItem('Kreatlo-username')
    localStorage.removeItem('Kreatlo-password')
    localStorage.removeItem('Kreatlo-jwtToken')
    localStorage.removeItem('Kreatlo-googleToken')
    dispatch(UserActions.reset())
    handleMenuItemClick('/')
  }

  const baseLinkClasses = 'tw-relative tw-inline-flex tw-items-center tw-h-16 md:tw-h-20 tw-px-1 md:tw-px-2 lg:tw-px-3 tw-text-sm tw-font-semibold tw-transition-colors tw-duration-200 focus:tw-outline-none'
  const hoverLineClasses = 'after:tw-content-[\'\'] after:tw-absolute after:tw-left-1/2 after:-tw-translate-x-1/2 after:tw-bottom-0 after:tw-h-[2px] after:tw-w-0 after:tw-transition-all after:tw-duration-300 hover:after:tw-w-3/4 focus-visible:after:tw-w-3/4'
  const activeNavLinkClasses = 'after:!tw-w-3/4'

  const currentDynamicLinkColor = actualShowSolidBg ? 'tw-text-teal' : 'tw-text-white'
  const highlightColor = 'tw-text-green'
  const hoverTextColorClass = 'hover:!tw-text-green focus-visible:!tw-text-green'

  const navItems: NavItemStructure[] = [
    { to: '/features', label: 'Funkcje' }, { to: '/pricing', label: 'Cennik' }, { to: '/get-quote', label: 'Pobierz Wtyczkę', highlight: false },
    {
      label: 'Branże',
      name: 'designers',
      children: [
        { to: '/designers?type=interior_design', label: 'Projektowanie wnętrz' },
        { to: '/designers?type=construction_design', label: 'Projektowanie konstrukcji' },
        { to: '/designers?type=garden_design', label: 'Projektowanie ogrodów' },
        { to: '/designers?type=design', label: 'Projektowanie' },
        { to: '/designers?type=other_services', label: 'Pozostałe usługi' }
      ]
    },
    { label: 'Strefa marek', name: 'brands', children: [{ to: '/products', label: 'Produkty' }, { to: '/brands', label: 'Marki' }] },
    { to: '/cooperation', label: 'Współpraca' },
    { to: '/blog', label: 'Blog' }
  ]

  const userMenuItems: NavItemStructure[] = [
    { to: '/user-profile', label: 'Profil' }, { to: '/messages', label: 'Wiadomości' }, { to: '/projects', label: 'Projekty' }, ...(isMinimumPackage(userPackage, Package.PREMIUM, packageExpiredAt) ? [{ to: '/tasks', label: 'Zadania' }] : []),
    ...(isSuperadmin ? [{ to: '/plants', label: 'Rośliny' }, { to: '/nurseries', label: 'Szkółki' }, { to: '/product-stats', label: 'Statystyki produktów' }] : []),
    { label: 'Wyloguj', action: handleLogout }
  ]

  const renderDropdownItems = (items: Array<{ to?: string, label: string, action?: () => void }>) => (
    items.map(item => (
      <li key={item.label}>
        {item.to
          ? (
            <Link to={item.to} onClick={() => handleMenuItemClick(item.to)} className='tw-block tw-px-4 tw-py-2 tw-text-sm tw-text-slate-700 hover:tw-bg-slate-100 dark:hover:tw-bg-slate-700 hover:!tw-text-green'>
              {item.label}
            </Link>
            )
          : (
            <button type='button' onClick={(item.action != null) ? () => { item.action!(); handleMenuItemClick() } : () => handleMenuItemClick()} className='tw-block tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-slate-700 hover:tw-bg-slate-100 dark:hover:tw-bg-slate-700 hover:!tw-text-green'>
              {item.label}
            </button>
            )}
      </li>
    ))
  )

  const renderNavLink = (item: NavItemStructure, isMobile: boolean = false) => {
    const isActive = item.to ? location.pathname === item.to || location.pathname.startsWith(item.to + '/') : false
    const linkColorForCurrentContext = item.highlight || isActive ? highlightColor : (actualShowSolidBg ? 'tw-text-teal' : 'tw-text-white')
    const lineSpecificColorForCurrentContext = item.highlight || isActive ? 'after:tw-bg-green' : (actualShowSolidBg ? 'after:tw-bg-green' : 'after:tw-bg-white')

    if ((item.children != null) && !isMobile) {
      return (
        <div
          key={item.name}
          className='tw-relative desktop-dropdown-wrapper group'
          onMouseEnter={() => handleDesktopDropdownMouseEnter(item.name!)}
          onMouseLeave={handleDesktopDropdownMouseLeave}
          data-dropdown-name={item.name}
        >
          <button type='button' aria-haspopup='true' aria-expanded={openDesktopDropdown === item.name} className={`${baseLinkClasses} ${linkColorForCurrentContext} ${openDesktopDropdown === item.name || (isActive && item.name === openDesktopDropdown) ? `${activeNavLinkClasses} ${highlightColor}` : ''} ${hoverLineClasses} ${lineSpecificColorForCurrentContext} ${hoverTextColorClass} tw-flex tw-items-center`}>
            <span>{item.label}</span>
            <ChevronDownIcon className={`${openDesktopDropdown === item.name ? 'tw-rotate-180' : ''} ${openDesktopDropdown === item.name || (isActive && item.name === openDesktopDropdown) ? highlightColor : linkColorForCurrentContext} group-hover:!tw-text-green`} />
          </button>
          {openDesktopDropdown === item.name && (
            <ul className='tw-absolute tw-left-0 tw-top-full tw-mt-[2px] tw-w-56 tw-origin-top-left tw-bg-white dark:tw-bg-slate-800 tw-rounded-b-md tw-shadow-lg tw-ring-1 tw-ring-black tw-ring-opacity-5 focus:tw-outline-none z-10'>
              <div className='tw-py-1'>
                {renderDropdownItems(item.children || [])}
              </div>
            </ul>
          )}
        </div>
      )
    }
    if (!isMobile && item.to) {
      return (
        <Link key={item.to} to={item.to} onClick={() => handleMenuItemClick(item.to)} className={`${baseLinkClasses} ${linkColorForCurrentContext} ${isActive ? `${activeNavLinkClasses} ${highlightColor}` : ''} ${hoverLineClasses} ${lineSpecificColorForCurrentContext} ${hoverTextColorClass}`}>
          {item.label}
        </Link>
      )
    }

    if ((item.children != null) && isMobile) {
      return (
        <div key={item.name}>
          <button type='button' onClick={() => toggleMobileDropdown(item.name!)} className={`tw-w-full tw-text-left tw-block tw-pl-3 tw-pr-4 tw-py-2 tw-rounded-md ${openMobileDropdown === item.name ? `${actualShowSolidBg ? 'tw-bg-slate-100 tw-text-green' : 'tw-bg-white/10 tw-text-green'}` : `${actualShowSolidBg ? 'tw-text-teal hover:tw-bg-slate-100' : 'tw-text-white hover:tw-bg-white/10'}`} tw-text-base tw-font-medium`}>
            <div className='tw-flex tw-justify-between tw-items-center'>
              <span>{item.label}</span>
              <ChevronDownIcon className={`${openMobileDropdown === item.name ? 'tw-rotate-180' : ''}`} />
            </div>
          </button>
          {openMobileDropdown === item.name && (
            <ul className='tw-mt-1 tw-pl-4 tw-space-y-1'>
              {renderDropdownItems(item.children || [])}
            </ul>
          )}
        </div>
      )
    }
    if (isMobile && item.to) {
      return (
        <Link key={item.to} to={item.to} onClick={() => handleMenuItemClick(item.to)} className={`tw-block tw-pl-3 tw-pr-4 tw-py-2 tw-rounded-md tw-text-base tw-font-medium ${isActive ? `${actualShowSolidBg ? 'tw-bg-green/10 tw-text-green' : 'tw-bg-white/10 tw-text-green'}` : `${actualShowSolidBg ? 'tw-text-teal hover:tw-bg-slate-100' : 'tw-text-white hover:tw-bg-white/10'}`}`}>
          {item.label}
        </Link>
      )
    }
    return null
  }

  return (
    <header ref={headerRef} className={`${headerPositioningClasses} ${headerBaseClasses} ${headerBackgroundClasses}`}>
      <nav className='tw-container xl:tw-max-w-7xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8'>
        <div className='tw-flex tw-items-center tw-justify-between tw-h-16 md:tw-h-20'>
          <Link className='tw-flex-shrink-0' to='/' onClick={() => handleMenuItemClick('/')}>
            <img src={actualShowSolidBg ? '/assets/images/logo/logo-black.png' : '/assets/images/logo/logo-white.png'} alt='Kreatlo Logo' className='tw-h-8 md:tw-h-10 tw-w-auto' />
          </Link>

          <div className='tw-hidden lg:tw-flex lg:tw-items-center lg:tw-space-x-1 xl:tw-space-x-2'>
            {navItems.map(item => renderNavLink(item, false))}
          </div>

          <div className='tw-flex tw-items-center'>
            <div className='tw-hidden lg:tw-flex lg:tw-items-center lg:tw-ml-6'>
              {!username
                ? (
                  <div className='tw-flex tw-items-center tw-space-x-1 xl:tw-space-x-2'>
                    <Link to='/register' onClick={() => handleMenuItemClick('/register')} className={`${baseLinkClasses} ${currentDynamicLinkColor} ${hoverLineClasses} ${actualShowSolidBg ? 'after:tw-bg-green' : 'after:tw-bg-white'} ${hoverTextColorClass}`}>Rejestracja</Link>
                    <Link to='/login' onClick={() => handleMenuItemClick('/login')} className={`tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors ${actualShowSolidBg ? 'tw-bg-green hover:tw-bg-opacity-90 tw-text-white' : 'tw-bg-white/20 hover:tw-bg-white/30 tw-text-white'}`}>Zaloguj się</Link>
                  </div>
                  )
                : (
                  <div
                    className={`
                            tw-relative desktop-dropdown-wrapper group tw-flex tw-items-center tw-h-16 md:tw-h-20
                            after:tw-content-[''] after:tw-absolute after:tw-left-1/2 after:-tw-translate-x-1/2 after:tw-bottom-0 after:tw-h-[2px] after:tw-transition-all after:tw-duration-300
                            after:tw-w-0 group-hover:after:tw-w-3/4 group-focus-within:after:tw-w-3/4
                            ${openDesktopDropdown === 'user' ? activeNavLinkClasses : ''}
                            ${actualShowSolidBg ? 'after:tw-bg-green' : 'after:tw-bg-white'}
                        `}
                    onMouseEnter={() => handleDesktopDropdownMouseEnter('user')}
                    onMouseLeave={handleDesktopDropdownMouseLeave}
                    data-dropdown-name='user'
                  >
                    <button
                      type='button'
                      aria-haspopup='true'
                      aria-expanded={openDesktopDropdown === 'user'}
                      className={`
                            tw-relative tw-inline-flex tw-items-center tw-h-auto tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-rounded-md
                            tw-transition-colors tw-duration-200 focus:tw-outline-none
                            ${openDesktopDropdown === 'user' ? highlightColor : currentDynamicLinkColor}
                            ${hoverTextColorClass}
                            ${!actualShowSolidBg
                              ? 'tw-bg-black/10 hover:tw-bg-black/25'
                              : (openDesktopDropdown === 'user' ? 'tw-bg-slate-100' : 'hover:tw-bg-slate-100')
                          }
                            focus-visible:tw-ring-1 ${actualShowSolidBg ? 'focus-visible:tw-ring-teal' : 'focus-visible:tw-ring-white/80'}
                          `}
                    >
                      <UserIcon
                          className={`
                              tw-mr-1.5
                              ${openDesktopDropdown === 'user' ? '!tw-text-green' : (actualShowSolidBg ? 'tw-text-teal' : 'tw-text-white')}
                              group-hover:!tw-text-green
                            `}
                        />
                      <span
                          className={`
                              tw-ml-0.5
                              ${openDesktopDropdown === 'user' ? '!tw-text-green' : ''}
                              group-hover:!tw-text-green
                            `}
                        >
                          {username}
                        </span>
                      <ChevronDownIcon
                          className={`
                              ${openDesktopDropdown === 'user' ? 'tw-rotate-180 !tw-text-green' : ''}
                              ${openDesktopDropdown === 'user' ? '' : (actualShowSolidBg ? 'tw-text-teal' : 'tw-text-white')}
                              group-hover:!tw-text-green
                            `}
                        />
                    </button>
                    {openDesktopDropdown === 'user' && (
                      <ul className='tw-absolute tw-right-0 tw-top-full tw-mt-[2px] tw-w-56 tw-origin-top-right tw-bg-white dark:tw-bg-slate-800 tw-rounded-b-md tw-shadow-lg tw-ring-1 tw-ring-black tw-ring-opacity-5 focus:tw-outline-none z-10'>
                          <div className='tw-py-1'>
                            {renderDropdownItems(userMenuItems)}
                          </div>
                        </ul>
                    )}
                  </div>
                  )}
            </div>
            <div className='lg:tw-hidden tw-ml-4'>
              <button ref={togglerButtonRef} type='button' className={`tw-p-2 tw-rounded-md tw-inline-flex tw-items-center tw-justify-center focus:tw-outline-none ${actualShowSolidBg ? 'tw-text-teal hover:tw-bg-slate-100' : 'tw-text-white hover:tw-bg-white/10'}`} aria-controls='mainNavigation' aria-expanded={isMobileMenuOpen} onClick={toggleMobileMenu}>
                <span className='tw-sr-only'>{isMobileMenuOpen ? 'Zamknij menu' : 'Otwórz menu'}</span>
                {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
              </button>
            </div>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div
            ref={mobileMenuRef}
            className={`
                  lg:tw-hidden tw-absolute tw-top-full tw-left-0 tw-right-0
                  tw-shadow-lg
                  ${actualShowSolidBg ? 'tw-bg-white' : 'tw-bg-dark-teal tw-bg-opacity-95'}
                  tw-overflow-y-auto
                  tw-max-h-[calc(100vh-4rem)] md:tw-max-h-[calc(100vh-5rem)]
              `}
            id='mainNavigation'
          >
            <div className='tw-pt-2 tw-pb-3 tw-space-y-1 tw-px-2 sm:tw-px-3'>
              {navItems.map(item => renderNavLink(item, true))}
            </div>
            <div className={`tw-pt-4 tw-pb-3 tw-border-t ${actualShowSolidBg ? 'tw-border-slate-200' : 'tw-border-white/20'}`}>
              {!username
                ? (
                  <div className='tw-space-y-1 tw-px-2 sm:tw-px-3'>
                    {renderNavLink({ to: '/register', label: 'Rejestracja' }, true)}
                    {renderNavLink({ to: '/login', label: 'Zaloguj się' }, true)}
                  </div>
                  )
                : (
                  <div className='tw-px-2 sm:tw-px-3'>
                    <button type='button' onClick={() => setIsMobileUserMenuOpen(!isMobileUserMenuOpen)} className={`tw-w-full tw-text-left tw-flex tw-items-center tw-justify-between tw-py-2 tw-px-3 tw-rounded-md ${actualShowSolidBg ? 'tw-text-teal hover:tw-bg-slate-100' : 'tw-text-white hover:tw-bg-white/10'}`} aria-expanded={isMobileUserMenuOpen}>
                      <div className='tw-flex tw-items-center'>
                          <UserIcon className={`${actualShowSolidBg ? 'tw-text-teal' : 'tw-text-white'}`} />
                          <span className={`tw-ml-3 tw-text-base tw-font-semibold ${actualShowSolidBg ? 'tw-text-teal' : 'tw-text-white'}`}>{username}</span>
                        </div>
                      <ChevronDownIcon className={`${isMobileUserMenuOpen ? 'tw-rotate-180' : ''} ${actualShowSolidBg ? 'tw-text-teal' : 'tw-text-white'}`} />
                    </button>
                    {isMobileUserMenuOpen && (
                      <ul className='tw-mt-1 tw-pl-7 tw-space-y-1 tw-list-none'>
                          {renderDropdownItems(userMenuItems)}
                        </ul>
                    )}
                  </div>
                  )}
            </div>
          </div>
        )}
      </nav>
    </header>
  )
}

export default Header
